import type { FC } from 'react';
import React, { Fragment, useState, useCallback, useContext } from 'react';

import { Box } from '@atlaskit/primitives/compiled/box';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { isErrorMarkedAsHandled } from '@confluence/graphql/entry-points/isErrorMarkedAsHandled.ts';
import { HOME_RIGHT_PANEL_FORGE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceStart,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import type { ForgeUIExtensionType } from '@confluence/forge-ui/entry-points/types.ts';
import { FORGE_MODULE_HOMEPAGE_FEED } from '@confluence/forge-ui/entry-points/ForgeModuleType.ts';
import {
	ForgeKeyboardShortcut,
	ForgeKeyboardShortcutVisualizer,
} from '@confluence/forge-ui/entry-points/ForgeKeyboardShortcut.ts';
import { HomepageFeedForgeApp } from '@confluence/forge-ui/entry-points/apps.ts';
import { extensionTitle } from '@confluence/forge-ui/entry-points/extensionTitle.ts';
import { LazyForgeUIExtensions } from '@confluence/forge-ui/entry-points/lazy.ts';

import { CollapsibleSection } from '../CollapsibleSection.tsx';

type CollapsibleForgeAppsProps = {
	forgeApps: ReadonlyArray<ForgeUIExtensionType>;
};

const CollapsibleForgeApps: FC<CollapsibleForgeAppsProps> = ({ forgeApps }) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const [appInitialStates, setAppInitialStates] = useState(new Map());

	const onInitialRender = useCallback(() => {
		experienceTracker.succeed({
			name: HOME_RIGHT_PANEL_FORGE_EXPERIENCE,
		});
	}, [experienceTracker]);

	return (
		<Fragment>
			{forgeApps.map((app: ForgeUIExtensionType) => (
				<ForgeKeyboardShortcutVisualizer key={app.id} module={app}>
					<CollapsibleSection
						sectionId={app.id}
						header={extensionTitle(app)}
						initialStateOverride={appInitialStates.get(app.id)}
					>
						<ExperienceStart name={HOME_RIGHT_PANEL_FORGE_EXPERIENCE} id={app.id} />
						<Box padding="space.100">
							<HomepageFeedForgeApp
								app={app}
								extensionData={{ type: FORGE_MODULE_HOMEPAGE_FEED }}
								onInitialRender={onInitialRender}
							/>
						</Box>
					</CollapsibleSection>
					<ForgeKeyboardShortcut
						module={app}
						action={() => {
							const oldValue = appInitialStates.get(app.id);
							const newValue = oldValue === 'EXPANDED' ? 'COLLAPSED' : 'EXPANDED';
							setAppInitialStates(new Map(appInitialStates.set(app.id, newValue)));
						}}
					/>
				</ForgeKeyboardShortcutVisualizer>
			))}
		</Fragment>
	);
};

export const ForgeApps: FC<{}> = () => {
	return (
		<LazyForgeUIExtensions
			moduleType={FORGE_MODULE_HOMEPAGE_FEED}
			render={(forgeModules, loading, error) => {
				if (loading) return null;

				if (error) {
					if (!isErrorMarkedAsHandled(error)) {
						return <ErrorDisplay error={error} />;
					}
					return null;
				}

				return (
					<ErrorBoundary attribution={Attribution.DISCO}>
						<CollapsibleForgeApps forgeApps={forgeModules} />
					</ErrorBoundary>
				);
			}}
		/>
	);
};
